import React from 'react'
import img from '../../assets/computer.png'
import { useEffect, useState } from "react";
import Logo from "../../assets/logo.png";
import video from "../../assets/5262343_Above_Aerial_Area_1920x1080.mp4";
import { PiEyeThin } from "react-icons/pi";
import { Link, useLocation } from "react-router-dom";
import { useMyContext } from "../../context/MyContext";
import { useNavigate } from "react-router-dom";
import { message, Alert } from "antd";
import { api } from '../../config/api';
import { toast } from 'react-toastify';


const Login = () => {
    const navigate = useNavigate();
    const { setRole, role, login, isAuthenticated } = useMyContext();

    const loc = useLocation()
    const [ispass, setispass] = useState(true);
    const [useremail, setuseremail] = useState("");
    const [password, setpassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [attemptedLogin, setAttemptedLogin] = useState(false);
    const [loginInProgress, setLoginInProgress] = useState(false);
    const [accountLocked, setAccountLocked] = useState(false);
    const [remainingAttempts, setRemainingAttempts] = useState(null);
    const [pending2FA, setPending2FA] = useState(false); // NEW: Track if waiting for 2FA

    const pass = () => {
        setispass(!ispass);
    };

    useEffect(() => {
        // If already authenticated and NOT waiting for 2FA, navigate to dashboard
        if (isAuthenticated && !loginInProgress && !pending2FA) {
            navigate('/dashboard');
        } else if (!loginInProgress && !pending2FA) {
            // Only load stored credentials if not in login process or 2FA flow
            const storedEmail = localStorage.getItem('userEmail');
            const storedPassword = localStorage.getItem('userPassword');

            if (storedEmail) setuseremail(storedEmail);
            if (storedPassword) setpassword(storedPassword);
            if (storedEmail || storedPassword) setRememberMe(true);
        }
    }, [isAuthenticated, navigate, loginInProgress, pending2FA]);

    const handleLogin = async () => {
        setAttemptedLogin(true);
        if (useremail === "" || password === "") {
            message.error("Please input credentials");
            return;
        }

        setLoading(true);
        setLoginInProgress(true);
        setAccountLocked(false);

        try {
            // Pre-emptively set timestamp for activity tracking
            localStorage.setItem('lastActivityTimestamp', Date.now().toString());

            // Handle remember me before login attempt
            if (rememberMe) {
                localStorage.setItem('userEmail', useremail);
                localStorage.setItem('userPassword', password);
            } else {
                localStorage.removeItem('userEmail');
                localStorage.removeItem('userPassword');
            }

            const res = await login(useremail, password);
            if (!res?.isSuccess) {
                // Check for remaining attempts first - it may be included with any error type
                if (res.remainingAttempts !== undefined) {
                    if (res.remainingAttempts <= 0) {
                        setAccountLocked(true);
                        setRemainingAttempts(null);
                        message.error("Account Locked");
                    } else {
                        setRemainingAttempts(res.remainingAttempts);

                        if (res.error && (res.error.code === "auth/wrong-password" || res.error.code === "auth/invalid-credential")) {
                            message.error(`Incorrect email or password. You have ${res.remainingAttempts} attempts remaining.`);
                        } else {
                            // For other errors that still return remaining attempts
                            message.error(`Login failed. You have ${res.remainingAttempts} ${res.remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining.`);
                        }
                    }
                } else {
                    // Errors without specific attempts count
                    if (res.error && (res.error.code === "auth/wrong-password" || res.error.code === "auth/invalid-credential")) {
                        message.error("Incorrect email or password.");
                    } else if (res.error && res.error.code === "auth/user-not-found") {
                        message.error("User not found");
                        setRemainingAttempts(null);
                    } else if (res.error && res.error.code === "auth/account-locked") {
                        setAccountLocked(true);
                        setRemainingAttempts(null);
                        message.error("Account Locked");
                    } else {
                        message.error("Login failed. Please check your credentials.");
                        setRemainingAttempts(null);
                    }
                }
                // Reset login progress flag on failure
                setLoginInProgress(false);
            } else {
                // Reset remaining attempts on successful login
                setRemainingAttempts(null);

                if (res.requires2fa) {
                    setPending2FA(true); // entering 2FA flow

                    // Build and persist 2FA context up-front so we can always navigate
                    const twoFaContext = {
                        userId: res?.data?.id,
                        email: res?.data?.email,
                        credentials: res.credentials,
                    };
                    sessionStorage.setItem('twofa_context', JSON.stringify(twoFaContext));

                    // Immediately clear loading and move to OTP screen for a smooth UX
                    setLoading(false);
                    setLoginInProgress(false);
                    navigate('/verify-otp', { state: twoFaContext });

                    // Fire-and-forget OTP send in the background; do NOT block navigation
                    (async () => {
                        try {
                            await api.post('/send-otp', {
                                userId: twoFaContext.userId,
                                email: twoFaContext.email,
                            });
                            toast.success("OTP has been sent to your registered email");
                        } catch (error) {
                            console.error('Error sending OTP', error);
                            const msg =
                                error?.response?.data?.message ||
                                'Unable to send a new OTP. If you already received a code, please use that.';
                            message.error(msg);
                        }
                    })();

                    return;
                }

                // Only navigate to dashboard if NOT requiring 2FA
                navigate("/dashboard");
            }
        } catch (error) {
            console.error("Login error: ", error);
            message.error({
                content: "An error occurred during login. Please try again.",
            });
            setLoginInProgress(false);
            setPending2FA(false);
        } finally {
            // Only reset loading if not navigating to 2FA
            if (!pending2FA) {
                setLoading(false);
            }
        }
    };

    return (
        <div>
            <div className="w-full flex min-h-screen">
                <div className="lg:w-1/2 w-full lg:flex hidden justify-center items-center min-h-screen relative">
                    <video autoPlay loop muted className="absolute inset-0 w-full h-full object-cover z-10">
                        <source src={video} />
                    </video>
                </div>
                <div className="lg:w-1/2 md:px-12 px-5 w-full bg-white flex flex-col justify-center items-center gap-4 min-h-screen">
                    <img src={Logo} alt='' className='py-4 w-1/5' />

                    <div className="space-y-2">
                        <h2 className="md:text-3xl text-2xl text-center font-bold text-[#3C404B]"> Log in to your account </h2>
                        <h2 className=" text-base font-normal text-center text-[#3C404B]"> Enter your email address and password to continue. </h2>
                    </div>

                    {accountLocked && (
                        <Alert
                            type="error"
                            message="Account Locked"
                            description={
                                <div>
                                    Your account has been locked due to too many failed login attempts. You can:
                                    <ul className="mt-2 list-disc pl-5">
                                        <li>Contact admin at operations@oneunionsolutions.com</li>
                                        <li>Click on "Forgot Password" below to reset your password</li>
                                        <li>Use your temporary password if provided</li>
                                    </ul>
                                </div>
                            }
                            className="w-4/5 mb-2"
                            showIcon
                            closable
                        />
                    )}

                    {remainingAttempts !== null && remainingAttempts < 4 && remainingAttempts > 0 && (
                        <Alert
                            type="warning"
                            message="Login Attempts"
                            description={`You have ${remainingAttempts} ${remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining before your account is locked.`}
                            className="w-4/5 mb-2"
                            showIcon
                            closable
                        />
                    )}

                    <div className="py-3 px-3 shadow-md flex flex-wrap justify-between gap-2 w-4/5 rounded-xl">
                        <Link
                            to="/"
                            className={`${loc.pathname === "/" ? "btnBg text-white" : ""
                                } text-sm text-black rounded-md text-center py-3 block md:w-[45%] btnHover hover:text-white `}
                        >
                            Log in
                        </Link>
                        <Link
                            to="/signup"
                            className={`${loc.pathname === "/signup" ? "btnBg text-white" : ""
                                } text-sm text-black rounded-md text-center py-3 block md:w-[45%] btnHover hover:text-white `}
                        >
                            Register
                        </Link>
                    </div>

                    <div className="w-full space-y-3">
                        <div className="flex flex-col gap-1 py-2 ">
                            <label className="w-full text-base font-normal text-[#3C404B]" htmlFor="username">
                                E-Mail Address
                            </label>
                            <input
                                onChange={(e) => setuseremail(e.target.value)}
                                value={useremail}
                                type="text"
                                className="w-full py-3 px-6 text-base rounded-md border-[#00000026] border outline-none active:border-cus-blue focus:border-cus-blue"
                                id="username"
                                placeholder="Enter your email"
                                disabled={loading}
                            />
                            {attemptedLogin && useremail === "" ? (
                                <span className="text-xs text-cus-red">Please input email</span>
                            ) : null}
                        </div>

                        <div className="flex flex-col gap-1 py-2 pt-0 ">
                            <label className="w-full text-base font-normal text-[#3C404B]" htmlFor="username">
                                Password
                            </label>
                            <div className="relative flex items-center">
                                <input
                                    onChange={(e) => setpassword(e.target.value)}
                                    value={password}
                                    type={ispass ? "password" : "text"}
                                    className="w-full py-3 px-6 text-base border rounded-md border-[#00000026] outline-none active:border-cus-blue focus:border-cus-blue"
                                    id="password"
                                    placeholder="Enter your password"
                                    onKeyDown={(e) => e.key === 'Enter' && !loading && handleLogin()}
                                    disabled={loading}
                                />
                                <button
                                    onClick={pass}
                                    className="absolute flex right-0 px-3 top-3 sm:top-4 text-cus-lblue"
                                    type="button"
                                    disabled={loading}
                                >
                                    <PiEyeThin />
                                </button>
                            </div>
                            {attemptedLogin && password === "" ? (
                                <span className="text-xs text-cus-red">Please input password</span>
                            ) : null}
                        </div>
                        <div className="flex items-center justify-between gap-2 py-3 text-xs">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    className="w-5"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    disabled={loading}
                                />
                                Remember me!
                            </label>
                            <Link to="/forgot-password" className="text-cus-red">
                                Forgot Password?
                            </Link>
                        </div>
                    </div>

                    <button
                        onClick={handleLogin}
                        className={`w-full text-sm text-white rounded-md py-3 btnBg ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        disabled={loading}
                    >
                        {loading ? 'Logging in...' : 'Log in'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Login